import moment from "moment";
import { formatCurrency, formatQuantity, formatLocalizedTime } from "../_common/functions";

export interface PurchaseOrderTemplateData {
  orderNumber: string | null;
  userName: string;
  confirmationNumber: string;
  vendorName: string;
  primaryPhone: string;
  primaryEmailAddress: string;
  createDate: string;
  locationName: string;
  expectedDeliveryDate: string;
  note: string;
  website: string;
  products: Array<{
    productName: string;
    orderBoxQuantity: number;
    vendorPerBoxValue: number;
    boxCost: number;
    totalCost: number;
    note: string;
  }>;
  subTotal: number;
  tax: number;
  fees: number;
  totalDue: number;
}

export const generatePurchaseOrderHTML = (data: PurchaseOrderTemplateData, language?: string): string => {
  const formatDate = (dateString: string) => {
    return dateString ? moment.utc(dateString).local().format("MM/DD/YY") : "N/A";
  };

  const formatDateTime = (dateString: string) => {
    return dateString
      ? `${moment.utc(dateString).local().format("MM/DD/YY")} ${formatLocalizedTime(moment.utc(dateString).local().toDate(), language)}`
      : "N/A";
  };

  const formatTime = (dateString: string) => {
    return dateString ? formatLocalizedTime(moment.utc(dateString).local().toDate(), language) : "N/A";
  };

  const productsRows = data.products?.map(product => `
    <tr>
      <td style="border: 1px solid #cccccc; padding: 8px; color: #000000; background-color: #ffffff;">
        ${product.productName}
      </td>
      <td style="border: 1px solid #cccccc; padding: 8px; color: #000000; background-color: #ffffff;">
        ${formatQuantity(product.orderBoxQuantity || 0)}
      </td>
      <td style="border: 1px solid #cccccc; padding: 8px; color: #000000; background-color: #ffffff;">
        ${formatQuantity(product.vendorPerBoxValue || 0)}
      </td>
      <td style="border: 1px solid #cccccc; padding: 8px; color: #000000; background-color: #ffffff;">
        $${formatCurrency(product.boxCost || 0)}
      </td>
      <td style="border: 1px solid #cccccc; padding: 8px; color: #000000; background-color: #ffffff;">
        $${formatCurrency(product.totalCost || 0)}
      </td>
      <td style="border: 1px solid #cccccc; padding: 8px; color: #000000; background-color: #ffffff;">
        ${product.note || "-"}
      </td>
    </tr>
  `).join('');

  return `
    <div style="color: #000000; background-color: #ffffff;">
      <!-- CSS Override Styles -->
      <style>
        * {
          color: #000000 !important;
          background-color: #ffffff !important;
          background-image: none !important;
          background: #ffffff !important;
          border-color: #cccccc !important;
          box-shadow: none !important;
          text-shadow: none !important;
          outline: none !important;
          filter: none !important;
          backdrop-filter: none !important;
        }
        
        html, body {
          background: #ffffff !important;
          background-color: #ffffff !important;
        }
        
        h1, h2, h3, h4, h5, h6 {
          color: #000000 !important;
          background-color: #ffffff !important;
          background: #ffffff !important;
        }
        
        p, span, div, td, th, tr {
          color: #000000 !important;
          background-color: #ffffff !important;
          background: #ffffff !important;
        }
        
        table {
          background-color: #ffffff !important;
          background: #ffffff !important;
        }
        
        th {
          background-color: #f5f5f5 !important;
          background: #f5f5f5 !important;
          color: #000000 !important;
        }
        
        .order-number-badge {
          background-color: #6b7280 !important;
          background: #6b7280 !important;
          color: #ffffff !important;
        }
        
        .border-top {
          border-top: 1px solid #000000 !important;
        }
        
        .bg-white, .bg-gray-50, .bg-gray-100, .bg-gray-200 { 
          background-color: #ffffff !important; 
          background: #ffffff !important;
        }
        .text-black, .text-gray-800, .text-gray-700, .text-gray-600 { 
          color: #000000 !important; 
        }
        .border-gray, .border-gray-300, .border-gray-200 { 
          border-color: #cccccc !important; 
        }
        
        :root {
          --tw-bg-opacity: 1 !important;
          --tw-text-opacity: 1 !important;
          --tw-border-opacity: 1 !important;
        }
        
        [style*="oklch"], [style*="lch"], [style*="lab"], [style*="hwb"] {
          color: #000000 !important;
          background-color: #ffffff !important;
          background: #ffffff !important;
        }
      </style>

      <!-- Header Section -->
      <div style="border-bottom: 2px solid #cccccc; padding-bottom: 20px; margin-bottom: 20px;">
        <h2 style="margin: 0; margin-bottom: 10px; color: #000000;">
          ORDER NUMBER: 
          <span class="order-number-badge" style="background-color: #6b7280; color: #ffffff; padding: 5px 10px; border-radius: 4px; font-weight: bold;">
            ${data.orderNumber ?? "Draft"}
          </span>
        </h2>
        <p style="margin: 5px 0; color: #000000;">
          <span style="font-weight: 600; color: #000000;">ORDERED BY:</span> ${data.userName}
        </p>
        <p style="margin: 5px 0; color: #000000;">
          <span style="font-weight: 600; color: #000000;">Confirmation Number:</span> ${data.confirmationNumber}
        </p>
      </div>
      
      <!-- Vendor Details Section -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin: 20px 0;">
        <div>
          <p style="margin: 8px 0; color: #000000;">
            <span style="font-weight: 600; color: #000000;">Vendor Name:</span> ${data.vendorName}
          </p>
          <p style="margin: 8px 0; color: #000000;">
            <span style="font-weight: 600; color: #000000;">Vendor Phone:</span> ${data.primaryPhone}
          </p>
          <p style="margin: 8px 0; color: #000000;">
            <span style="font-weight: 600; color: #000000;">Vendor Email:</span> ${data.primaryEmailAddress}
          </p>
          <p style="margin: 8px 0; color: #000000;">
            <span style="font-weight: 600; color: #000000;">Order Date:</span> ${formatDate(data.createDate)}
          </p>
          <p style="margin: 8px 0; color: #000000;">
            <span style="font-weight: 600; color: #000000;">Order Time:</span> ${formatTime(data.createDate)}
          </p>
        </div>
        <div>
          <p style="margin: 8px 0; color: #000000;">
            <span style="font-weight: 600; color: #000000;">Ship To:</span> ${data.locationName}
          </p>
          <p style="margin: 8px 0; color: #000000;">
            <span style="font-weight: 600; color: #000000;">Expected Delivery:</span> ${formatDateTime(data.expectedDeliveryDate)}
          </p>
          <p style="margin: 8px 0; color: #000000;">
            <span style="font-weight: 600; color: #000000;">Note:</span> ${data.note}
          </p>
          <p style="margin: 8px 0; color: #000000;">
            <span style="font-weight: 600; color: #000000;">Vendor Website:</span> ${data.website}
          </p>
        </div>
      </div>
      
      <!-- Products Section -->
      <div style="margin-top: 30px;">
        <h3 style="margin: 0 0 15px 0; font-weight: 600; color: #000000;">PRODUCTS INFO</h3>
        <table style="width: 100%; border-collapse: collapse; margin: 15px 0; background-color: #ffffff;">
          <thead>
            <tr style="background-color: #f5f5f5;">
              <th style="border: 1px solid #cccccc; padding: 8px; text-align: left; font-weight: bold; color: #000000; background-color: #f5f5f5;">Product Name</th>
              <th style="border: 1px solid #cccccc; padding: 8px; text-align: left; font-weight: bold; color: #000000; background-color: #f5f5f5;">Order Box Qty</th>
              <th style="border: 1px solid #cccccc; padding: 8px; text-align: left; font-weight: bold; color: #000000; background-color: #f5f5f5;">Qty/Wt Per Box</th>
              <th style="border: 1px solid #cccccc; padding: 8px; text-align: left; font-weight: bold; color: #000000; background-color: #f5f5f5;">Box Cost</th>
              <th style="border: 1px solid #cccccc; padding: 8px; text-align: left; font-weight: bold; color: #000000; background-color: #f5f5f5;">Total Cost</th>
              <th style="border: 1px solid #cccccc; padding: 8px; text-align: left; font-weight: bold; color: #000000; background-color: #f5f5f5;">Note</th>
            </tr>
          </thead>
          <tbody>
            ${productsRows}
          </tbody>
        </table>
      </div>
      
      <!-- Summary Section -->
      <div style="margin-top: 20px; text-align: right;">
        <div style="margin: 5px 0; color: #000000;">
          <span style="font-weight: 600; color: #000000;">Sub Total:</span> $${formatCurrency(data.subTotal || 0)}
        </div>
        <div style="margin: 5px 0; color: #000000;">
          <span style="font-weight: 600; color: #000000;">Tax:</span> $${formatCurrency(data.tax || 0)}
        </div>
        <div style="margin: 5px 0; color: #000000;">
          <span style="font-weight: 600; color: #000000;">Fees:</span> $${formatCurrency(data.fees || 0)}
        </div>
        <div class="border-top" style="margin: 5px 0; font-weight: bold; border-top: 1px solid #000000; padding-top: 5px; color: #000000;">
          <span style="font-weight: 600; color: #000000;">Total Due:</span> $${formatCurrency(data.totalDue || 0)}
        </div>
      </div>
    </div>
  `;
};